// Design: Premium dark theme, teal #00D4AA accent, amber highlights
// Consistent with existing service pages, hero, what-we-do grid, comparison,
// how-we-work steps, AnimatedStatGrid, See-it-in-action proof block,
// pillar-spoke links, FAQ accordion, CTA strip.
// Schema: WebPage + Service + FAQPage + BreadcrumbList JSON-LD

import { Link } from "wouter";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import { AnimatedStatGrid } from "@/components/AnimatedStat";
import {
  ArrowRight,
  ShieldCheck,
  FileText,
  AlertTriangle,
  Scale,
  Search,
  CheckCircle2,
  ChevronDown,
  Lock,
  BookOpen,
} from "lucide-react";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/Footer";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SharePostRow from "@/components/SharePostRow";
import SeriesGuideLink from "@/components/SeriesGuideLink";


import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import SchemaScript from "@/components/SchemaScript";
const pageTitle = "AI Governance Agency: EU AI Act and GDPR for B2B";
const pageDescription = "AI governance frameworks, guardrails and audit trails for B2B teams deploying agentic AI, covering the EU AI Act, GDPR, FCA and sector-specific rules.";
const pageHeading = "AI Governance Agency for B2B Marketing: EU AI Act, GDPR and FCA";
const lockedAnswer = "AI governance for B2B marketing is the set of policies, review gates, audit records and technical guardrails that control what AI tools and agents may draft, publish, spend or decide. Integrated.Social designs it and builds it into your agentic workflows, so each claim has a source, an owner and a named senior person who signs it off.";
const answerBlock = `${lockedAnswer} It suits regulated B2B teams deploying agents; we do not give legal advice, and the first step is a 30-minute call.`;
const faqSummary = "These answers cover what AI governance means for a marketing team, how it applies to the EU AI Act, GDPR and FCA rules, how review gates and audit records work, how long a first deployment takes and why a policy document alone is not enough. In short, governance is built into each workflow before agents go live, and a named senior person signs off what is published.";
const serviceAudience = [
  { "@type": "BusinessAudience", audienceType: "B2B companies" },
  { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
];
const faqs = [
  {
    q: "What is AI governance for B2B marketing?",
    a: lockedAnswer,
  },
  {
    q: "Why does my business need an AI governance framework before deploying agentic AI?",
    a: "Agentic AI systems make autonomous decisions and produce outputs at scale. Without governance, a single misconfigured agent can publish non-compliant content, expose sensitive data, or generate regulatory liability across hundreds of outputs before anyone notices. A governance framework defines what agents can and cannot do, who reviews edge cases, how outputs are audited, and what the escalation path is when something goes wrong. It is the difference between controlled deployment and uncontrolled risk.",
  },
  {
    q: "How does the EU AI Act affect B2B marketing operations?",
    a: "The EU AI Act’s obligations depend on intended use, provider or deployer role, and territorial scope. Ordinary marketing automation is not automatically high-risk. Certain AI interactions and content carry transparency duties, subject to exceptions. The Commission’s current timeline distinguishes Annex III high-risk rules from product-linked systems; there is no single deadline for every deployment. We map applicable duties and dates with your legal team, rather than treating a screener or senior sign-off as legal certification.",
  },
  {
    q: "What is the difference between AI safety and AI governance?",
    a: "AI safety focuses on preventing harmful model outputs, hallucinations, bias, toxic content, and adversarial attacks. AI governance is broader: it encompasses safety plus compliance, accountability, transparency, audit trails, and organizational policy. Safety is a technical property of a model or system; governance is an organizational capability that ensures AI is deployed responsibly, documented appropriately, and aligned with legal and ethical obligations across the full lifecycle.",
  },
  {
    q: "What compliance guardrails do you build into agentic AI deployments?",
    a: "We implement layered guardrails: output classifiers that flag potential issues before publication, human review gates, role-based access controls, audit logs and screener agents that check content against configured sector-specific rules. These checks support compliance review; they do not guarantee compliance or replace required approvals. A named senior person signs off what is published. Every deployment includes a governance runbook documenting the control architecture, with permissions, review responsibilities and escalation paths agreed for the deployment.",
  },
  {
    q: "How do you handle AI governance in regulated industries like fintech or BetTech?",
    a: "Controls depend on the business, use case and jurisdiction. Fintech workflows can include applicable FCA financial promotion checks, GDPR data minimization and DORA documentation where DORA applies. BetTech workflows can include responsible gambling content and audience controls where those advertising rules apply. Screeners check configured rules; a named senior person signs off publication, and any required regulatory approval remains with the appropriately authorized firm. These controls support compliance review rather than guarantee it.",
  },
  {
    q: "Can you retrofit governance onto an existing AI marketing stack?",
    a: "Yes. We audit your current AI tools and workflows, identify governance gaps, missing audit trails, uncontrolled agent permissions, undocumented data flows, and implement controls without requiring a full rebuild. The retrofit typically takes 3-6 weeks depending on stack complexity and includes a governance register, updated data processing agreements, and a monitoring dashboard. This is a planning estimate, not a guaranteed delivery window; scope and timing are confirmed after reviewing the deployment. The framework applies to future production; earlier outputs may still need review.",
  },
  {
    q: "Is a policy PDF the same as this engagement?",
    a: "No. A policy document on its own sits in a board folder. The build is a screener on the output, an audit log, a risk tier, a permission matrix, and a register legal can sign before production. For a multi-market BetTech workflow, the appropriate legal review comes before production on a multi-agent system. If you only need a slide, this is the wrong service.",
  },
  {
    q: 'How should a B2B team control agent identities and permissions?',
    a: 'Assign each workflow a named owner, defined purpose, least-privilege access and an auditable record of important actions. Keep publication, spending and customer-data permissions separate from research and drafting rights, review access regularly, and maintain a practical way to pause or revoke a workflow.',
  },
  {
    q: 'What records should an AI governance program retain?',
    a: 'Retain proportionate records of the use case, owner, data inputs, permission scope, approved claims, review decisions, material actions and incident response. The exact obligations depend on the deployment, contracts and applicable law, so legal and security teams should define the organization’s retention policy.',
  }
];

export default function ServiceAIGovernance() {

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/ai-governance/#webpage",
        url: "https://integrated.social/services/ai-governance/",
        name: pageTitle,
        headline: pageHeading,
        description: pageDescription,
        abstract: answerBlock,
        isPartOf: { "@id": "https://integrated.social/#website" },
        about: { "@id": "https://integrated.social/#organization" },
        publisher: { "@id": "https://integrated.social/#organization" },
        breadcrumb: { "@id": "https://integrated.social/services/ai-governance/#breadcrumb" },
        author: { "@id": "https://integrated.social/modi-elnadi/#person" },
        inLanguage: "en-US",
        dateModified: "2026-11-02",
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/ai-governance/#service",
        url: "https://integrated.social/services/ai-governance/",
        name: "AI Governance, Safety & Regulatory Compliance for B2B",
        provider: { "@id": "https://integrated.social/#organization" },
        serviceType: "AI Governance Framework Design & Implementation",
        description: "AI governance, safety and regulatory compliance for marketing teams at B2B companies and B2C brands.",
        audience: serviceAudience,
        areaServed: [
          { "@type": "Country", name: "United Kingdom" },
          { "@type": "Country", name: "United States" },
          { "@type": "Place", name: "European Union" },
          { "@type": "Place", name: "Global" },
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "AI Governance Services",
          itemListElement: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "AI Governance Framework Design & Policy Documentation",
                description: "AI governance framework design and policy documentation for marketing teams at B2B companies and B2C brands.",
                audience: serviceAudience,
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Compliance Guardrail Architecture for Agentic AI",
                description: "Compliance guardrail architecture for agentic AI used by marketing teams at B2B companies and B2C brands.",
                audience: serviceAudience,
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "EU AI Act & GDPR Compliance Audit for Marketing AI",
                description: "EU AI Act and GDPR compliance audits for marketing AI at B2B companies and B2C brands.",
                audience: serviceAudience,
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Regulated Industry AI Deployment (FCA, DORA, ASA)",
                description: "Regulated-industry AI deployment governance for marketing teams at B2B companies and B2C brands.",
                audience: serviceAudience,
              },
            },
          ],
        },
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/ai-governance/#faq",
        isPartOf: { "@id": "https://integrated.social/services/ai-governance/#webpage" },
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://integrated.social/services/ai-governance/#breadcrumb",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://integrated.social/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://integrated.social/services/",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: pageTitle,
            item: "https://integrated.social/services/ai-governance/",
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        ogImage="https://integrated.social/media/integrated-social-logo_c362b0f0.webp"
        description={pageDescription}
        canonical="https://integrated.social/services/ai-governance/"
        locale="en-US"
      />
      <SchemaScript schema={JSON.stringify(schemaData)} />
      <div className="min-h-screen bg-background">
        <Header />
        <main>

        {/* Breadcrumb */}
        <nav className="container pt-24 pb-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <li>
              <Link href="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link href="/services/" className="hover:text-foreground transition-colors">
                Services
              </Link>
            </li>
            <li>/</li>
            <li className="text-foreground font-medium">{pageTitle}</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="container pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-4xl">
              <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#00D4AA] border border-[#00D4AA]/30 rounded-full mb-6">
                Regulated Industry Specialist
              </span>
              <h1 className="font-serif headline-xl mb-6">
                {pageHeading}
              </h1>
              <p data-answer-block className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-8">
                {answerBlock}
              </p>
              <p className="text-muted-foreground mb-8">AI governance, safety and regulatory compliance for marketing teams at B2B companies and B2C brands. Agents draft; a named senior person signs off.</p>
              <div className="flex flex-wrap gap-4">
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_ai_governance"); trackSectionCtaClick({ section_name: "service_ai_governance_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Book your AI Governance Audit <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  href="/blog/ai-governance-2026-quality-control/"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200"
                >
                  Read: AI Governance in 2026
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex flex-col gap-4">
              {/* Visual: governance framework diagram */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: ShieldCheck, label: "Compliance Guardrails", color: "text-[#00D4AA]", bg: "bg-[#00D4AA]/10 border-[#00D4AA]/20" },
                  { icon: FileText, label: "Audit Trail Architecture", color: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/20" },
                  { icon: Scale, label: "Regulatory Alignment", color: "text-[#00D4AA]", bg: "bg-[#00D4AA]/10 border-[#00D4AA]/20" },
                  { icon: Lock, label: "Agent Access Controls", color: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/20" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={`border rounded-xl p-5 flex flex-col items-center text-center gap-3 ${item.bg}`}
                  >
                    <item.icon className={`w-8 h-8 ${item.color}`} />
                    <span className="text-sm font-semibold text-foreground">{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="border border-[#00D4AA]/20 bg-[#00D4AA]/5 rounded-xl p-5 text-center">
                <p className="text-sm text-muted-foreground">
                  Covering{" "}
                  <span className="font-semibold text-foreground">EU AI Act · GDPR · FCA · DORA · ASA</span>
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* What We Implement */}
        <section className="bg-[#1a1a1a] text-white py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">What we implement</h2>
            <p className="text-gray-400 text-lg max-w-2xl mb-12">
              Not policy documents that gather dust. Operational governance architectures embedded
              directly into your AI deployment, enforced at the agent level, not the boardroom.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: ShieldCheck,
                  title: "Compliance Guardrail Architecture",
                  desc: "Output classifiers and screener agents that screen AI-generated assets against configured sector-specific checks, FCA financial promotions, GDPR data handling, ASA advertising standards, before publication.",
                },
                {
                  icon: FileText,
                  title: "Audit Trail & Logging Systems",
                  desc: "Audit logs of material agent actions, data access and outputs, with agreed retention and access controls. Structured for review, timestamped, attributed and exportable. Supports relevant DORA documentation where DORA applies.",
                },
                {
                  icon: Scale,
                  title: "EU AI Act Risk Classification",
                  desc: "We map your AI tools and workflows against the Act’s risk categories, identify applicable obligations and dates, and support the documentation your role requires.",
                },
                {
                  icon: AlertTriangle,
                  title: "Agentic AI Guardrails & Permissions",
                  desc: "Role-based access controls limiting which agents can access which data and take which actions. Human-in-the-loop review gates for high-risk decisions. Escalation paths when agents encounter edge cases.",
                },
                {
                  icon: Search,
                  title: "AI Governance Audit & Gap Analysis",
                  desc: "A structured audit of your current AI marketing stack, identifying undocumented data flows, missing consent mechanisms, uncontrolled agent permissions, and regulatory exposure before regulators find them.",
                },
                {
                  icon: BookOpen,
                  title: "Governance Runbook & Policy Documentation",
                  desc: "A complete governance register: what AI systems you operate, what data they process, what decisions they make, who is accountable, and what the review and update cadence is. Designed for board-level sign-off.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-6 border border-white/10 rounded-lg hover:border-[#00D4AA]/40 transition-colors"
                >
                  <item.icon className="w-8 h-8 text-[#00D4AA] mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text="AI Governance and Safety for B2B Enterprises. We help organizations implement responsible AI frameworks, risk assessment protocols, and governance structures that support meeting board, regulatory, and customer requirements. From AI policy design to vendor assessment and model risk management, we build the governance layer that makes enterprise AI deployment defensible."
            title="AI Governance and Safety for B2B Enterprises"
            readingTime={5}
          />
        </div>

        {/* Governed vs Ungoverned */}
        <section className="py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-12">Governed vs ungoverned AI</h2>
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-accent/40"><tr><th className="px-4 py-3 text-left text-red-500">Ungoverned AI deployment</th><th className="px-4 py-3 text-left text-[#00D4AA]">Governed AI deployment (what we build)</th></tr></thead>
                <tbody className="divide-y divide-border text-muted-foreground">
                  {[
                    ["Agents publish non-compliant content at scale before review", "Compliance screener checks outputs against configured rules before publication"],
                    ["No audit trail, impossible to reconstruct what AI decided or why", "Audit records of material actions, attributed and timestamped"],
                    ["Regulatory exposure discovered during inspection, not before", "Regulatory risk mapped and mitigated before deployment"],
                    ["Brand risk from hallucinated claims or biased outputs", "Output classifiers flag hallucinations and brand violations"],
                    ["Data processing agreements don't cover AI tool usage", "Data processing agreements updated to cover AI workflows"],
                    ["No escalation path when agents encounter edge cases", "Defined escalation paths and human-in-the-loop review gates"],
                  ].map(([ungoverned, governed]) => <tr key={ungoverned}><td className="px-4 py-4">{ungoverned}</td><td className="px-4 py-4 text-foreground">{governed}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="bg-accent/50 py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-12">
              How we build your governance framework
            </h2>
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-background"><tr><th className="px-4 py-3 text-left">Step</th><th className="px-4 py-3 text-left">Stage</th><th className="px-4 py-3 text-left">Deliverables</th></tr></thead>
                <tbody className="divide-y divide-border text-muted-foreground">
                  {[
                    ["01", "AI Stack Audit", "We map every AI tool, agent, and automated workflow in your marketing and sales operations, documenting data flows, decision points, outputs, and current control gaps."],
                    ["02", "Risk Classification", "We classify each system against EU AI Act risk tiers and applicable sector regulations (FCA, GDPR, DORA, ASA), identifying high-risk obligations and compliance deadlines."],
                    ["03", "Guardrail Architecture", "We design and implement technical controls: output classifiers, access permission matrices, audit logging, human review gates, and compliance screener agents embedded in your workflows."],
                    ["04", "Governance Register and Runbook", "We deliver a complete governance register, policy documentation, and a maintenance runbook, so your team can operate, update, and maintain controls and evidence for compliance review."],
                  ].map(([step, name, output]) => <tr key={step}><td className="px-4 py-4 text-[#00D4AA] font-mono">{step}</td><td className="px-4 py-4 text-foreground font-semibold">{name}</td><td className="px-4 py-4">{output}</td></tr>)}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">This framework is built before an <Link href="/services/gemini-agentic-ai/" className="text-[#00D4AA] hover:underline">agentic deployment</Link> goes into production.</p>
          </div>
        </section>

        {/* Regulatory Coverage Stats */}
        <section className="py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-8">Regulatory coverage</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mb-10">
              Governance frameworks we design are aligned to the regulatory landscape B2B
              organizations operating in the UK, EU, and US actually face.
            </p>
            <AnimatedStatGrid
              eyebrow="Framework Coverage"
              stats={[
                {
                  value: 5,
                  label: "Regulatory frameworks covered",
                  sub: "EU AI Act · GDPR · FCA · DORA · ASA",
                  isStatic: true,
                },
                {
                  value: 3,
                  suffix: "–6 wks",
                  label: "Planning estimate, not guaranteed",
                  sub: "Scope and timing confirmed after stack review",
                  isStatic: true,
                },
                {
                  value: "2027–2028",
                  label: "EU AI Act high-risk application dates",
                  sub: "2 Dec 2027: Annex III · 2 Aug 2028: product-linked systems",
                  isStatic: true,
                },
              ]}
              cols={3}
              className="mb-4"
            />
          </div>
        </section>

        {/* See it in action, Agentic AI GTM regulated industry */}
        <section className="py-20 bg-[#0D1220]">
          <div className="container">
            <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/30 rounded-full px-4 py-1.5 text-teal-400 text-xs font-semibold tracking-widest uppercase mb-8">
              See It In Action
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
                  Governance-First Agentic AI in a Regulated Industry
                </h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  A regulated sports media and BetTech infrastructure provider needed to deploy a
                  multi-agent AI orchestration system across three European markets, each with
                  different advertising regulations, language requirements, and compliance
                  obligations.
                </p>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Before a single agent went live, we designed a governance architecture that
                  embedded compliance directly into the orchestration layer. A dedicated compliance
                  screener agent screened outputs against configured jurisdiction-specific checks before
                  delivery. Role-based permissions prevented agents from accessing data outside
                  their defined scope. Material agent actions were recorded with attribution.
                </p>
                <p className="text-slate-300 leading-relaxed mb-6">
                  The design supports a governed AI content workflow for persona-specific assets in
                  three languages, with a governance register submitted to the client's legal
                  team before production. Approval and delivery timing depend on the agreed scope.
                </p>

                <div className="flex flex-wrap gap-3 items-center">
                  <Link
                    href="/case-studies/agentic-ai-gtm/"
                    className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-black font-bold px-6 py-3 rounded-xl text-sm transition-all hover:scale-105 active:scale-95"
                  >
                    Read the full case study <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    href="/about/"
                    className="inline-flex items-center gap-2 border border-teal-500/40 text-teal-400 hover:border-teal-400 hover:text-teal-300 px-5 py-3 rounded-xl text-sm font-medium transition-all"
                  >
                    Meet the strategist behind the framework →
                  </Link>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  {
                    metric: "Bounded",
                    label: "Agents with defined compliance scope",
                    sub: "Each agent's data access and output permissions explicitly bounded",
                  },
                  {
                    metric: "3",
                    label: "Jurisdictions in the stated deployment scope",
                    sub: "UK · Spain · Portugal, different ad rules, one governance layer",
                  },
                  {
                    metric: "Review",
                    label: "Legal review before production",
                    sub: "Approval timing depends on scope and the appropriate review",
                  },
                ].map((r) => (
                  <div
                    key={r.metric}
                    className="bg-white/[0.04] border border-teal-500/20 rounded-xl p-5 flex items-start gap-5"
                  >
                    <div className="stat-number text-3xl text-teal-400 min-w-[4rem]">
                      {r.metric}
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm">{r.label}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{r.sub}</div>
                    </div>
                  </div>
                ))}
                <div className="bg-teal-500/5 border border-teal-500/20 rounded-xl p-5">
                  <div className="text-xs font-mono text-teal-500/60 uppercase tracking-widest mb-2">
                    Sector
                  </div>
                  <div className="text-white text-sm font-semibold">
                    Sports Media & BetTech Infrastructure · Regulated Industry · Europe
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pillar-Spoke Links */}
        <section className="bg-[#1a1a1a] text-white py-16">
          <div className="container">
            <h2 className="font-serif text-2xl md:text-3xl mb-8">Related services & reading</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link
                href="/services/gemini-agentic-ai/"
                className="block p-5 border border-white/10 rounded-lg hover:border-[#00D4AA]/40 transition-colors"
              >
                <span className="text-xs uppercase tracking-wider text-[#00D4AA]">Service</span>
                <h3 className="text-lg font-semibold mt-2">Gemini Enterprise Agentic AI</h3>
                <p className="text-gray-400 text-sm mt-2">
                  Deploy governed multi-agent systems for B2B marketing and sales.
                </p>
              </Link>
              <Link
                href="/services/ai-marketing-strategy/"
                className="block p-5 border border-white/10 rounded-lg hover:border-[#00D4AA]/40 transition-colors"
              >
                <span className="text-xs uppercase tracking-wider text-[#00D4AA]">Service</span>
                <h3 className="text-lg font-semibold mt-2">AI Marketing Strategy & Content Ops</h3>
                <p className="text-gray-400 text-sm mt-2">
                  Persona-driven GTM with responsible AI content production.
                </p>
              </Link>
              <Link
                href="/services/seo-aeo-geo/"
                className="block p-5 border border-white/10 rounded-lg hover:border-[#00D4AA]/40 transition-colors"
              >
                <span className="text-xs uppercase tracking-wider text-[#00D4AA]">Service</span>
                <h3 className="text-lg font-semibold mt-2">AI Search Optimization (AEO/GEO)</h3>
                <p className="text-gray-400 text-sm mt-2">
                  Review AI-answer visibility with compliant, entity-rich content.
                </p>
              </Link>
              <Link
                href="/blog/ai-governance-2026-quality-control/"
                className="block p-5 border border-white/10 rounded-lg hover:border-[#00D4AA]/40 transition-colors"
              >
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">AI Governance in 2026: Quality Control</h3>
                <p className="text-gray-400 text-sm mt-2">
                  Practical frameworks for governing AI outputs at scale.
                </p>
              </Link>
              <Link
                href="/blog/five-eyes-ai-cybersecurity-warning-frontier-models-b2b-2026/"
                className="block p-5 border border-white/10 rounded-lg hover:border-[#00D4AA]/40 transition-colors"
              >
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">Five Eyes AI Cybersecurity Warning</h3>
                <p className="text-gray-400 text-sm mt-2">
                  What the frontier model security advisory means for B2B AI deployments.
                </p>
              </Link>
              <Link
                href="/about/"
                className="block p-5 border border-white/10 rounded-lg hover:border-[#00D4AA]/40 transition-colors"
              >
                <span className="text-xs uppercase tracking-wider text-gray-500">About</span>
                <h3 className="text-lg font-semibold mt-2">Modi Elnadi, 20+ Years in Digital Marketing</h3>
                <p className="text-gray-400 text-sm mt-2">
                  Regulated industry deployments from fintech to BetTech.
                </p>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20">
          <div className="container max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl mb-8">Frequently asked questions</h2>
            <p data-faq-summary className="text-muted-foreground leading-relaxed mb-8">{faqSummary}</p>
            <div className="space-y-4">
              <FaqAccordion
                  items={faqs}
                  headingLevel={3}
                  className="space-y-4"
                  itemClassName="border border-border rounded-lg overflow-hidden"
                  buttonClassName="w-full flex items-center justify-between p-5 text-left hover:bg-accent/50 transition-colors"
                  questionClassName="m-0"
                  answerClassName="px-5 pb-5 text-muted-foreground leading-relaxed"
                  iconClassName="w-5 h-5 shrink-0 transition-transform duration-200"
                />
            </div>
            <p data-source-context className="text-sm text-muted-foreground leading-relaxed mt-8">Regulatory context checked November 2, 2026: <a href="https://ai-act-service-desk.ec.europa.eu/en/ai-act/timeline/timeline-implementation-eu-ai-act" className="text-[#00D4AA] hover:underline">European Commission AI Act timeline</a>, <a href="https://www.fca.org.uk/publications/good-poor-practice/ai-financial-services" className="text-[#00D4AA] hover:underline">FCA AI oversight guidance</a>, and <a href="https://www.esma.europa.eu/esmas-activities/digital-finance-and-innovation/digital-operational-resilience-act-dora" className="text-[#00D4AA] hover:underline">ESMA DORA scope</a>. These sources describe regulatory context, not proof of any deployment’s compliance. We do not give legal advice.</p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#00D4AA] py-16">
          <div className="container text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-black mb-4">
              Ready to govern your AI deployment?
            </h2>
            <p className="text-black/70 text-lg mb-8 max-w-2xl mx-auto">
              We audit your current AI stack, map your regulatory exposure, and implement the
              governance architecture that makes agentic AI deployable. Not just possible.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                href="https://calendly.com/modielnadi/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-900 transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_ai_governance"); trackSectionCtaClick({ section_name: "service_ai_governance_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
              >
                Book your AI Governance Audit <ArrowRight className="w-4 h-4" />
              </a>
              <a aria-label="Email Integrated.Social at info@integrated.social"
                href="mailto:info@integrated.social"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-black text-black font-semibold rounded-lg hover:bg-black/10 transition-all duration-200"
              >
                Email us
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
      <Footer />

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  See exactly where your brand appears in Google AI Mode, ChatGPT, Perplexity, and Gemini. Free AI Visibility Score with a scored report and gap analysis.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_ai_governance_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                >
                  Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalized</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about where you are, where you want to be, and what a realistic 90-day AI marketing deployment looks like for your business. No deck. No sales script.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_ai_governance"); trackSectionCtaClick({ section_name: "service_ai_governance_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
        <SeriesGuideLink
          part={2}
          title="Human-in-the-Loop AI Content Governance"
          slug="human-in-the-loop-ai-content-governance"
        />
        </main>
      </div>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="AI Governance and Responsible AI"
          canonicalUrl="https://integrated.social/services/ai-governance/"
          excerpt="Governance frameworks that keep your AI deployments compliant, auditable, and commercially safe."
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
